/**
 * Job Center — global admin view of every async KAPE job.
 * Route: /kcis/admin/job-center
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

const STATUS_COLORS = {
  queued: "#F59E0B", running: "#3B82F6", retrying: "#F59E0B",
  completed: "#10B981", failed: "#EF4444", cancelled: "#6B7280",
  stale: "#EF4444",
};

export default function JobCenter() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const reload = useCallback(async () => {
    try {
      const q = filter ? `?status=${encodeURIComponent(filter)}` : "";
      const { data } = await api.get(`/kcis/admin/kape/job-center${q}`);
      setData(data);
    } catch { setData({ items: [], counts: {} }); }
  }, [filter]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
  }, [reload]);

  const cancel = async (id) => {
    try {
      await api.post(`/kcis/admin/kape/job-center/${id}/cancel`);
      toast.success("Cancelled");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const retry = async (id) => {
    try {
      await api.post(`/kcis/admin/kape/generation-jobs/${id}/retry`);
      toast.success("Requeued");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };

  return (
    <KcisAdminShell>
      <div className="max-w-7xl mx-auto px-8 py-10" data-testid="job-center-page">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KCIS · Operations
            </div>
            <h1 className="text-3xl font-semibold mt-1">Job Center</h1>
            <p className="mt-2 text-sm max-w-2xl" style={{ color: PAL.muted }}>
              Every asynchronous KAPE generation job across every course. Retry, cancel, and inspect diagnostics.
            </p>
          </div>
          <button onClick={reload} className="rounded-full px-3 py-2 text-xs border"
                  style={{ borderColor: PAL.border, color: PAL.text }} data-testid="jc-refresh">
            Refresh
          </button>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mt-8">
          {["queued", "running", "retrying", "completed", "failed", "cancelled", "stale"].map((k) => (
            <button key={k} onClick={() => setFilter(filter === k ? "" : k)}
                    className={`rounded-xl p-3 border text-left ${filter === k ? "ring-2" : ""}`}
                    style={{ borderColor: PAL.border, background: PAL.panel,
                              boxShadow: filter === k ? `0 0 0 2px ${STATUS_COLORS[k]}` : "none" }}
                    data-testid={`jc-filter-${k}`}>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: STATUS_COLORS[k] }}>{k}</div>
              <div className="text-2xl font-semibold mt-0.5">{data?.counts?.[k] || 0}</div>
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-8 rounded-2xl border overflow-hidden" style={{ borderColor: PAL.border, background: PAL.panel }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase" style={{ color: PAL.muted, background: "#0F0B14" }}>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Progress</th>
                <th className="px-4 py-2">Course</th>
                <th className="px-4 py-2">Target</th>
                <th className="px-4 py-2">Step</th>
                <th className="px-4 py-2">Ref</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data === null && (
                <tr><td colSpan={8} className="px-4 py-6 text-sm" style={{ color: PAL.muted }}>Loading…</td></tr>
              )}
              {data && data.items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-sm" style={{ color: PAL.muted }}>No jobs matching the filter.</td></tr>
              )}
              {data && data.items.map((j) => (
                <tr key={j.id} className="border-t" style={{ borderColor: PAL.border }} data-testid={`jc-row-${j.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{j.job_type}</td>
                  <td className="px-4 py-2">
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded"
                          style={{ background: STATUS_COLORS[j.status] || "#6B7280", color: "white" }}>
                      {j.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">{j.progress_percent || 0}%</td>
                  <td className="px-4 py-2 text-xs">
                    <Link to={`/kcis/admin/university/course-builder/${j.course_id}`}
                          style={{ color: PAL.accent }}>{j.course_id.slice(0, 8)}</Link>
                  </td>
                  <td className="px-4 py-2 text-xs">{(j.target_id || "").slice(0, 8) || "—"}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: PAL.muted }}>{j.current_step}</td>
                  <td className="px-4 py-2 text-[10px] font-mono" style={{ color: PAL.muted }}>{j.id.slice(0, 8)}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {["queued", "running", "retrying"].includes(j.status) && (
                        <button onClick={() => cancel(j.id)} className="text-xs px-2 py-1 rounded"
                                style={{ background: "#DC2626", color: "white" }}
                                data-testid={`jc-cancel-${j.id}`}>Cancel</button>
                      )}
                      {["failed", "stale", "cancelled"].includes(j.status) && (
                        <button onClick={() => retry(j.id)} className="text-xs px-2 py-1 rounded"
                                style={{ background: PAL.accent, color: "#0F0B14" }}
                                data-testid={`jc-retry-${j.id}`}>Retry</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </KcisAdminShell>
  );
}
